import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Button,
  Indicators,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Textarea,
} from 'ui'
import { Wand2 } from 'lucide-react'
import { useCurrentUser } from '../../../queries/useCurrentUser'
import { useAdminProfileById } from '../../../queries/useAdminProfileById'
import { useUpdateProfileMutation } from '../../../queries/useUpdateProfileMutation'
import { useGenerateProfileMutation } from '../../../queries/useGenerateProfileMutation'
import { useSkipOnboarding } from '../../../hooks'
import clsx from 'clsx'
import { useNavigate } from 'react-router-dom'
import { normalizeProfile } from '../../../utils/profileHelpers'
import { useQueryClient } from '@tanstack/react-query'

const schema = yup.object({
  publicBio: yup.string().required('Cannot be blank'),
})
export type ProfessionalProfilePart3 = yup.InferType<typeof schema>

interface ProfessionalStepThreeProps {
  onSuccess?: () => void
  hideNavigation?: boolean
  editMode?: boolean
  profileId?: string // Added profileId parameter
  targetField?: string
}

export const ProfessionalStepThree: React.FC<ProfessionalStepThreeProps> = ({
  onSuccess,
  hideNavigation = false,
  editMode = false,
  profileId,
  targetField,
}) => {
  // Use admin profile query if profileId is provided, otherwise use current user
  const { data: user, isLoading: isCurrentUserLoading } = useCurrentUser()
  const {
    data: adminProfile,
    isLoading: isAdminProfileLoading,
  } = useAdminProfileById(profileId)

  // Determine which profile data to use
  const rawProfileData = profileId ? adminProfile : user
  const normalizedProfile = normalizeProfile(rawProfileData || null)
  const isLoading = profileId ? isAdminProfileLoading : isCurrentUserLoading
  const userId = normalizedProfile?.userId || ''

  const navigate = useNavigate()
  const skipOnboarding = useSkipOnboarding()
  const form = useForm<ProfessionalProfilePart3>({
    defaultValues: {
      publicBio: '',
    } as ProfessionalProfilePart3,
    reValidateMode: 'onChange',
    resolver: yupResolver(schema as any) as any,
  })

  const generateProfileMutation = useGenerateProfileMutation()
  const [generatedBio, setGeneratedBio] = useState(null as string | null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [hasMounted, setHasMounted] = useState(false)

  const handleGenerateDescription = async () => {
    if (!normalizedProfile || generateProfileMutation.isPending) return

    try {
      // Clear existing text before generation starts
      form.setValue('publicBio', '')
      setGeneratedBio('')

      // METHOD 1: Using the callback approach (simpler)
      // Comment out this section if you want to use Method 2 instead

      // const result = await generateProfileMutation.mutateAsync({
      //   data: normalizedProfile,
      //   options: {
      //     stream: true,
      //     onChunk: (chunk) => {
      //       // Update text incrementally as each chunk arrives
      //       if (chunk.trim()) {
      //         form.setValue('publicBio', (prev) => {
      //           const newValue = (prev || '') + chunk;
      //           setGeneratedBio(newValue);
      //           return newValue;
      //         });
      //       }
      //     },
      //     onComplete: (fullText) => {
      //       console.log('Bio generation complete:', fullText.substring(0, 50) + '...');
      //     }
      //   }
      // });

      // METHOD 2: Direct stream consumption (more control)
      // Uncomment this section and comment out Method 1 to use this approach
      setIsGenerating(true)
      console.log('Generating profile...')
      console.log(normalizedProfile)
      const result = await generateProfileMutation.mutateAsync({
        data: rawProfileData, // Use raw data for best compatibility with API
        options: { stream: true },
      })

      // If we have a stream, read from it directly
      if (result.stream) {
        const reader = result.stream.getReader()

        try {
          // Start with empty bio text
          let bioText = ''

          // Read chunks until the stream is done
          while (true) {
            const { done, value } = await reader.read()

            if (done) {
              console.log('Stream reading complete')
              setIsGenerating(false)
              break
            }

            // Update UI with each chunk
            if (value) {
              bioText += value
              setGeneratedBio(bioText)
              form.setValue('publicBio', bioText)
            }
          }
        } catch (error) {
          console.error('Error reading stream:', error)
        }
      } else {
        // Fallback to the complete text if streaming isn't available
        console.log('Stream not available, using complete text')
        setGeneratedBio(result.introduction)
        form.setValue('publicBio', result.introduction)
        setIsGenerating(false)
      }
    } catch (error) {
      console.error('Failed to generate bio:', error)
    }
  }

  useEffect(() => {
    if (!normalizedProfile || generateProfileMutation.isPending || hasMounted) {
      return
    }

    // Set form value from existing profile data
    const existingBio = normalizedProfile?.publicBio || ''
    form.setValue('publicBio', existingBio)
    setHasMounted(true)

    // Only auto-generate if bio is genuinely empty (not just whitespace)
    const hasExistingBio = existingBio && existingBio.trim().length > 0

    if (!hasExistingBio && !generatedBio) {
      handleGenerateDescription()
    }
  }, [
    normalizedProfile,
    generateProfileMutation.isPending,
    hasMounted,
    generatedBio,
    // Removed function references and state setters
  ])

  const { mutateAsync: updateProfile } = useUpdateProfileMutation()
  const queryClient = useQueryClient()
  const handleSubmitForm = (redirect?: string) => async (
    values: ProfessionalProfilePart3
  ) => {
    await updateProfile({
      userId: userId,
      publicBio: values.publicBio as string,
    })
    queryClient.invalidateQueries({ queryKey: ['currentUser'] })

    // Call onSuccess callback if provided
    if (onSuccess) {
      onSuccess()
    }

    // Only navigate if redirect is provided and we're not in edit mode
    if (redirect && !editMode) {
      navigate(redirect)
    }
  }

  const onSaveDraft = async () => {
    // Try to save current form data as best-effort, but always skip regardless of validation
    const values = form.getValues()
    try {
      await handleSubmitForm(undefined)(values)
    } catch {
      // Ignore errors - we're skipping anyway
    }
    skipOnboarding()
  }

  const [useOriginal, setUseOriginal] = useState(false)
  const onSwapBio = () => {
    if (!useOriginal) {
      form.setValue(
        'publicBio',
        normalizedProfile?.publicBio ?? normalizedProfile?.about ?? ''
      )
    } else {
      form.setValue('publicBio', generatedBio ?? '')
    }
    setUseOriginal(!useOriginal)
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(
          handleSubmitForm(editMode ? undefined : '/onboard/professional/four')
        )}
        className={clsx(isLoading && 'opacity-50')}
      >
        <div className="flex flex-col">
          <FormLabel>
            Here's How Your Profile Will Appear to Educators.
          </FormLabel>
          <p className="font-body text-sm mt-3">
            Based on everything you've told us in the previous steps, we've
            created a brief description that teachers will see when they're
            looking for professionals to inspire their students. (That's you!)
            Feel free to add, edit, or start from scratch!{' '}
          </p>
          <div className="flex flex-col mt-8 bg-gradient-to-b from-white to-primary/5 p-4 rounded-lg border-primary/10 border p-6 relative">
            <FormLabel>Name</FormLabel>
            <p className="mb-4 font-body text-sm">
              {normalizedProfile?.firstName} {normalizedProfile?.lastName}
            </p>
            <FormLabel>Profile Bio</FormLabel>
            <FormField
              control={form.control}
              name="publicBio"
              render={({ field }) => (
                <FormItem className="w-full col-span-3 mt-2 relative">
                  <FormControl>
                    <Textarea {...field} className="min-h-[150px]" />
                  </FormControl>
                  {targetField === 'publicBio' && (
                    <div className="absolute -right-8 top-1/2 transform -translate-y-1/2">
                      <div className="w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            {isGenerating ? (
              <Indicators.AbsoluteOverlay bgClassName="bg-white/50" />
            ) : null}
            {normalizedProfile?.about?.length ? (
              <Button
                type="button"
                variant="link"
                className="text-actionable underline text-sm py-4 mt-4"
                onClick={onSwapBio}
              >
                <span>
                  Reset to {useOriginal ? 'generated' : 'original'} bio
                </span>
              </Button>
            ) : null}
            <div className="flex justify-between items-center">
              <Button
                type="button"
                size="sm"
                className="ml-auto text-sm flex items-center gap-2"
                onClick={handleGenerateDescription}
                disabled={generateProfileMutation.isPending}
              >
                {generateProfileMutation.isPending ? (
                  <Indicators.Spinner size="sm" />
                ) : (
                  <Wand2 className="h-4 w-4" />
                )}
                <span>Generate with AI</span>
              </Button>
            </div>
          </div>
        </div>

        <div className="flex flex-col mt-8">
          <FormLabel className="mt-8">Ready?</FormLabel>
          <p className="font-body text-sm mt-3">
            Were you expecting this to be more difficult? That's not how
            Educator PRO Connect works! Click that Activate button below and
            you'll be on your way.
          </p>
          <p className="font-body text-sm text-body/70 mt-3">
            PS. Once your profile is created, you can add a photo, set
            availability, change your password, and more. Just go into your
            Profile Settings to see what's available.
          </p>
        </div>

        {!hideNavigation ? (
          <div className="flex-col flex mt-12 w-full">
            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              variant="default"
              className="mx-auto uppercase"
            >
              {form.formState.isSubmitting ? (
                <Indicators.Spinner
                  size="lg"
                  className="mt-auto mr-2 mb-auto ml-auto"
                />
              ) : null}
              <span>Save My Profile &gt;</span>
            </Button>
            <Button
              type="button"
              variant="link"
              className="text-actionable underline text-sm"
              onClick={onSaveDraft}
            >
              <span>Save Draft and Finish Later</span>
            </Button>
          </div>
        ) : (
          <div className="flex-col flex mt-8 w-full">
            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              variant="default"
              className="ml-auto"
            >
              {form.formState.isSubmitting ? (
                <Indicators.Spinner
                  size="lg"
                  className="mt-auto mr-2 mb-auto ml-auto"
                />
              ) : null}
              <span>{editMode ? 'Update Profile' : 'Save Changes'}</span>
            </Button>
          </div>
        )}
      </form>
    </Form>
  )
}
